import { LanguageCode, TimestampNumber } from '@/types/common';

export const DATE_FORMAT = {
  FULL: 'full', //한국 형식: YYYY.MM.DD AM H:MM
  FULL_WEEKDAY: 'full-weekday', //한국 형식: YY.MM.DD (W) AM H:MM
  DATE: 'date', //한국 형식: YYYY.MM.DD
} as const;

export type DateFormatType = (typeof DATE_FORMAT)[keyof typeof DATE_FORMAT];
export type DateOrder = 'YMD' | 'DMY' | 'MDY';

const LOCALE_MAP: Record<LanguageCode, string> = {
  ko: 'ko-KR',
  en_US: 'en-US',
  ja: 'ja-JP',
  zh: 'zh-CN',
  de: 'de-DE',
  es: 'es-ES',
  ru: 'ru-RU',
  fr: 'fr-FR',
  sv: 'sv-SE',
  it: 'it-IT',
  hi: 'hi-IN',
  vi: 'vi-VN',
  id: 'id-ID',
  th: 'th-TH',
  ms: 'ms-MY',
  nl: 'nl-NL',
  pt: 'pt-BR',
  tr: 'tr-TR',
  pl: 'pl-PL',
  ar: 'ar-SA',
  zh_TW: 'zh-TW',
} as const;

const DATE_ORDER_MAP: Record<LanguageCode, DateOrder> = {
  ko: 'YMD',
  en_US: 'MDY',
  ja: 'YMD',
  zh: 'YMD',
  de: 'DMY',
  es: 'DMY',
  ru: 'DMY',
  fr: 'DMY',
  sv: 'YMD',
  it: 'DMY',
  hi: 'DMY',
  vi: 'DMY',
  id: 'DMY',
  th: 'YMD',
  ms: 'DMY',
  nl: 'DMY',
  pt: 'DMY',
  tr: 'DMY',
  pl: 'DMY',
  ar: 'DMY',
  zh_TW: 'YMD',
};

const HOUR24_LOCALES = new Set(['de', 'ru', 'fr', 'sv', 'it', 'nl', 'tr', 'pl', 'id']);
const PERIOD_AFTER_LOCALES = new Set(['en_US', 'es', 'hi', 'vi', 'ms', 'ar', 'pt']);

export const formatDateByLocale = (
  timestamp: TimestampNumber,
  type: DateFormatType,
  localeKey: LanguageCode
): string => {
  const date = new Date(timestamp);
  const locale = LOCALE_MAP[localeKey] ?? 'ko-KR';
  const hour12 = !HOUR24_LOCALES.has(localeKey);
  const dateOrder = DATE_ORDER_MAP[localeKey] ?? 'YMD';

  const parts = new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    weekday: 'short',
    hour: 'numeric',
    minute: '2-digit',
    hour12,
  }).formatToParts(date);

  const getPart = (partType: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === partType)?.value ?? '';

  const year = getPart('year');
  const shortYear = year.slice(-2);
  const month = getPart('month');
  const day = getPart('day');
  const weekday = getPart('weekday');

  const hour = getPart('hour');
  const minute = getPart('minute');
  const dayPeriod = getPart('dayPeriod');

  const formattedDate = formatDate(dateOrder, year, month, day);
  const formattedShortDate = formatDate(dateOrder, shortYear, month, day);
  const formattedTime = formatTimeByLocale(localeKey, hour, minute, dayPeriod);

  switch (type) {
    case DATE_FORMAT.FULL:
      return `${formattedDate} ${formattedTime}`;
    case DATE_FORMAT.FULL_WEEKDAY:
      return `${formattedShortDate} (${weekday}) ${formattedTime}`;
    case DATE_FORMAT.DATE:
      return formattedDate;
    default:
      return '';
  }
};

const formatDate = (order: DateOrder, year: string, month: string, day: string): string => {
  switch (order) {
    case 'YMD':
      return `${year}.${month}.${day}`;
    case 'DMY':
      return `${day}.${month}.${year}`;
    case 'MDY':
      return `${month}.${day}.${year}`;
    default:
      return '';
  }
};

export const formatTimeByLocale = (
  localeKey: LanguageCode,
  hour: string,
  minute: string,
  dayPeriod: string
): string => {
  if (HOUR24_LOCALES.has(localeKey)) {
    return `${hour}:${minute}`;
  }

  if (PERIOD_AFTER_LOCALES.has(localeKey)) {
    return `${hour}:${minute} ${dayPeriod}`;
  }

  return `${dayPeriod} ${hour}:${minute}`;
};
